import { useEffect, useState } from 'react';
import * as extraditionMlaService from '../../../services/extraditionMlaCaseRequests';
import type { LookupItem } from '../../../mocks/criminalMasterData';

export type ExtraditionMlaLookupScope = 'step1' | 'step2' | 'step3' | 'step4' | 'step5' | 'step6' | 'review';

type LookupBundle = {
  provinces: LookupItem[];
  districts: LookupItem[];
  countries: LookupItem[];
  nationalities: LookupItem[];
  genders: LookupItem[];
  custodyStatuses: LookupItem[];
  urgencyLevels: LookupItem[];
  deliveryMethods: LookupItem[];
};

const EMPTY: LookupBundle = {
  provinces: [],
  districts: [],
  countries: [],
  nationalities: [],
  genders: [],
  custodyStatuses: [],
  urgencyLevels: [],
  deliveryMethods: [],
};

let sharedLookups: LookupBundle = { ...EMPTY };

export function useExtraditionMlaLookups(scope: ExtraditionMlaLookupScope, provinceId?: string) {
  const [data, setData] = useState<LookupBundle>(sharedLookups);
  const [loading, setLoading] = useState(scope !== 'step5' && scope !== 'step6');

  useEffect(() => {
    if (scope === 'step6') {
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(scope !== 'step5');

    const load = async (): Promise<Partial<LookupBundle>> => {
      if (scope === 'step5') {
        const del = await extraditionMlaService.getDeliveryMethods();
        return { deliveryMethods: del.success ? del.data : [] };
      }
      if (scope === 'step1') {
        const [prov, countries] = await Promise.all([
          extraditionMlaService.getProvinces(),
          extraditionMlaService.getCountries(),
        ]);
        return {
          provinces: prov.success ? prov.data : [],
          countries: countries.success ? countries.data : [],
        };
      }
      if (scope === 'step2') {
        const [nationalities, countries, genders, custodyStatuses] = await Promise.all([
          extraditionMlaService.getNationalities(),
          extraditionMlaService.getCountries(),
          extraditionMlaService.getGenders(),
          extraditionMlaService.getCustodyStatuses(),
        ]);
        return {
          nationalities: nationalities.success ? nationalities.data : [],
          countries: countries.success ? countries.data : [],
          genders: genders.success ? genders.data : [],
          custodyStatuses: custodyStatuses.success ? custodyStatuses.data : [],
        };
      }
      if (scope === 'step4') {
        const urg = await extraditionMlaService.getUrgencyLevels();
        return { urgencyLevels: urg.success ? urg.data : [] };
      }
      const [prov, countries, nationalities, genders, custodyStatuses, urg, del] = await Promise.all([
        extraditionMlaService.getProvinces(),
        extraditionMlaService.getCountries(),
        extraditionMlaService.getNationalities(),
        extraditionMlaService.getGenders(),
        extraditionMlaService.getCustodyStatuses(),
        extraditionMlaService.getUrgencyLevels(),
        extraditionMlaService.getDeliveryMethods(),
      ]);
      return {
        provinces: prov.success ? prov.data : [],
        countries: countries.success ? countries.data : [],
        nationalities: nationalities.success ? nationalities.data : [],
        genders: genders.success ? genders.data : [],
        custodyStatuses: custodyStatuses.success ? custodyStatuses.data : [],
        urgencyLevels: urg.success ? urg.data : [],
        deliveryMethods: del.success ? del.data : [],
      };
    };

    void load().then((partial) => {
      if (!mounted) return;
      sharedLookups = { ...sharedLookups, ...partial };
      setData({ ...sharedLookups });
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [scope]);

  useEffect(() => {
    if (scope !== 'step1' && scope !== 'review') return;
    if (!provinceId) {
      setData((d) => ({ ...d, districts: [] }));
      return;
    }

    let mounted = true;
    void extraditionMlaService.getDistricts(provinceId).then((r) => {
      if (!mounted) return;
      const districts = r.success ? r.data : [];
      sharedLookups = { ...sharedLookups, districts };
      setData((d) => ({ ...d, districts }));
    });
    return () => {
      mounted = false;
    };
  }, [scope, provinceId]);

  return {
    loading,
    provinces: data.provinces,
    districts: data.districts,
    countries: data.countries,
    nationalities: data.nationalities,
    genders: data.genders,
    custodyStatuses: data.custodyStatuses,
    urgencyLevels: data.urgencyLevels,
    deliveryMethods: data.deliveryMethods,
  };
}
